import { useIsMutating } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { CheckCircle2, Lock } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DetailSection } from "@/components/shared/detail-section";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { StatusPill } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useFeedbackSentiments } from "@/hooks/use-review-feedback";
import {
  ReviewRoundClosedError,
  type ReviewTaskCriterion,
  type ReviewTaskDetail,
  saveReviewResponseKey,
  useReviewTask,
  useSaveReviewResponse,
  useSubmitReviewTask,
} from "@/hooks/use-review-tasks";
import { categoryColorVar } from "@/lib/category-colors";
import { FEEDBACK_PROMPTS } from "@/lib/reviews/feedback-prompts";
import { SCALE_NOT_APPLICABLE_ORDINAL } from "@/lib/reviews/scale";
import { RatingSlider } from "./RatingSlider";
import { ReviewEndorsements } from "./ReviewEndorsements";
import { ReviewFeedbackEditor } from "./ReviewFeedbackEditor";

/**
 * Filling in one review — EPIC-061.
 *
 * Answers save as they are given, so there is nothing to lose by navigating
 * away and nothing to remember to press. Handing in is the only irreversible
 * act on the page, and it says so.
 *
 * The rating control is a row of rungs rather than the slider WI-220 asks for:
 * there is no slider primitive in `components/ui`, and a slider whose stops
 * carry different meanings per role and level is a worse first cut than named
 * buttons. The slider is a later refinement over the same data.
 */
export function ReviewTaskForm({ reviewerId }: { reviewerId: string }) {
  const { data, isLoading, isError, error, refetch, isFetching, isFetchedAfterMount } =
    useReviewTask(reviewerId);
  /**
   * Whether the cards may be seeded yet.
   *
   * Each card copies its answer into local state once, on mount, so it must
   * mount on data that includes everything already saved. Coming back to a
   * review rendered the CACHED copy from the previous visit: ratings looked
   * fine (they were often from earlier still) but comments typed last time
   * came back empty, though the server had them. So wait for a fetch made on
   * this visit, and for any save still in flight from the last one — a comment
   * flushed on the way out lands after the page has already reopened.
   *
   * Latched: once the cards are up, later saves and refetches must not swap
   * the form for a skeleton under the reviewer's cursor.
   */
  const savesInFlight = useIsMutating({ mutationKey: saveReviewResponseKey(reviewerId) });
  const [seeded, setSeeded] = useState(false);
  if (!seeded && isFetchedAfterMount && !isFetching && savesInFlight === 0) setSeeded(true);
  const submit = useSubmitReviewTask(reviewerId);
  const navigate = useNavigate();
  /**
   * What has been answered so far, held here as well as on the server.
   *
   * Without it the "Hand in" button reads its answers from the last fetch, so
   * rating everything left it disabled until the page was reloaded — which is
   * exactly what happened the first time this was clicked through. Refetching
   * after every rating would fix it too, at the cost of a round trip per click
   * and a race with whatever is being typed. The server still decides on
   * submit, and names anything missing, so this cannot wave a bad one through.
   */
  const [answers, setAnswers] = useState<Record<string, number | null>>({});
  /**
   * The unsigned note, held HERE and nowhere else until hand-in.
   *
   * Not autosaved like everything else on this page, and that is the whole
   * point: a draft on the server is a row that says who is writing it, which
   * is exactly what this note must not have.
   *
   * Declared above the loading and error returns because hooks must run in the
   * same order on every render — which is also why the sentiment list is
   * fetched unconditionally rather than only when the form is unlocked.
   */
  const sentiments = useFeedbackSentiments();
  const [anonymousBody, setAnonymousBody] = useState("");
  const [anonymousSentiment, setAnonymousSentiment] = useState("");

  if (isError) {
    /*
      A round that has finished is not a failure, and "Try again" on one is a
      button that cannot work. Say what happened and point at where the answers
      live now — the results page — rather than showing a red error.
    */
    if (error instanceof ReviewRoundClosedError) {
      return (
        <EmptyState
          icon={CheckCircle2}
          title="This round has closed"
          description={`${error.message} Results are on your Reviews page once a round you were part of has finished.`}
          action={
            <Button variant="outline" asChild>
              <Link to="/dashboard/reviews">Go to Reviews</Link>
            </Button>
          }
        />
      );
    }
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Couldn't load this review"}
        onRetry={() => refetch()}
      />
    );
  }
  if (isLoading || !data || !seeded) return <Skeleton className="h-96 w-full" />;

  const locked = data.statusKey === "complete";
  /**
   * Notes outlive hand-in; the round closing is what ends them. Two different
   * locks on one page, so each is named for what it actually gates.
   */
  const roundClosed = data.cycleStatusKey !== "open";

  /** Both fields or neither — an untagged note cannot be stored, and a tag
   * with nothing under it is not a note. */
  const anonymousReady = anonymousBody.trim().length > 0 && anonymousSentiment.length > 0;
  const anonymousNote = { body: anonymousBody.trim(), sentimentKey: anonymousSentiment };
  const answerFor = (criterion: ReviewTaskCriterion) =>
    criterion.questionId in answers ? answers[criterion.questionId] : criterion.ordinal;
  const unanswered = data.criteria.filter((c) => c.isRequired && answerFor(c) === null);

  return (
    <div className="space-y-6">
      {locked && (
        <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/50 p-4">
          <Lock className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <div className="text-sm">
            <p className="font-medium">Handed in</p>
            <p className="text-muted-foreground">
              This review has been submitted, so it can no longer be changed. If something needs
              correcting, ask HR.
            </p>
          </div>
        </div>
      )}

      {data.instructions && (
        <DetailSection title="Before you start">
          <p className="whitespace-pre-line text-sm">{data.instructions}</p>
        </DetailSection>
      )}

      {data.criteria.map((criterion) => (
        <CriterionCard
          key={criterion.questionId}
          reviewerId={reviewerId}
          criterion={criterion}
          scale={data.scale}
          locked={locked}
          onAnswer={(ordinal) =>
            setAnswers((current) => ({ ...current, [criterion.questionId]: ordinal }))
          }
        />
      ))}

      {/*
        The written half. Notes stay editable after hand-in and until the
        round closes — the manager does not read the report until then, so
        there is no window in which words change under a reader. The ratings
        above lock at hand-in as they always have.
      */}
      {/*
        Above the free-text section: endorsing is choosing from what was
        already asked, so it belongs beside the ratings rather than after the
        prose.
      */}
      <ReviewEndorsements reviewerId={reviewerId} locked={locked} />

      <DetailSection title="Anything else you want to say?">
        <ReviewFeedbackEditor
          reviewerId={reviewerId}
          locked={roundClosed}
          criteria={data.criteria.map((c) => ({ criterionId: c.questionId, name: c.name }))}
        />

        {!locked && (
          <div className="mt-6 border-border border-t pt-4">
            <p className="font-medium">{FEEDBACK_PROMPTS.anonymous}</p>
            <p className="mt-0.5 text-muted-foreground text-sm">
              Optional, and only sent when you hand in. Your name is never recorded against it — not
              hidden, not stored — so you won't be able to find it or change it afterwards.
            </p>
            <div className="mt-2 space-y-2">
              <Select value={anonymousSentiment} onValueChange={setAnonymousSentiment}>
                <SelectTrigger
                  className="w-full sm:w-64"
                  aria-label="How the unsigned note is meant"
                >
                  <SelectValue placeholder="How is this meant?" />
                </SelectTrigger>
                <SelectContent>
                  {(sentiments.data ?? []).map((sentiment) => (
                    <SelectItem key={sentiment.key} value={sentiment.key}>
                      {sentiment.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Textarea
                value={anonymousBody}
                onChange={(event) => setAnonymousBody(event.target.value)}
                placeholder="Left blank, nothing is recorded."
                rows={3}
              />
            </div>
          </div>
        )}
      </DetailSection>

      {!locked && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-4">
          <div className="text-sm">
            <p className="font-medium">
              {unanswered.length === 0
                ? "Everything required is answered."
                : `${unanswered.length} still to rate`}
            </p>
            <p className="text-muted-foreground">
              {/* Said plainly, because it is the one thing that cannot be undone. */}
              Once you hand this in you won't be able to change it.
            </p>
          </div>
          {/*
            Confirmed, not just warned about. The sentence beside the button
            said it could not be undone and the button did it on one click
            anyway — which is the shape of a mis-click, not of a decision.
          */}
          <ConfirmDialog
            trigger={<Button disabled={unanswered.length > 0 || submit.isPending}>Hand in</Button>}
            title="Hand in this review?"
            description={
              data.relationshipKey === "self"
                ? "Your answers go to your manager and you won't be able to change them. If something needs correcting afterwards, ask HR."
                : `Your answers about ${data.subjectName} are final once handed in. If something needs correcting afterwards, ask HR.`
            }
            cancelLabel="Keep editing"
            confirmLabel="Hand it in"
            onConfirm={() =>
              submit.mutate(anonymousReady ? anonymousNote : null, {
                // Back to the list: the next thing to do is the next review,
                // and the "Handed in" page here has nothing left to act on.
                onSuccess: () => navigate({ to: "/dashboard/reviews" }),
              })
            }
          />
        </div>
      )}
    </div>
  );
}

function CriterionCard({
  reviewerId,
  criterion,
  scale,
  locked,
  onAnswer,
}: {
  reviewerId: string;
  criterion: ReviewTaskCriterion;
  scale: ReviewTaskDetail["scale"];
  locked: boolean;
  /** Tells the form what was chosen, so "Hand in" knows without a refetch. */
  onAnswer: (ordinal: number | null) => void;
}) {
  const save = useSaveReviewResponse(reviewerId);
  const sensitiveId = useId();
  const expectationPanelId = useId();
  const [ordinal, setOrdinal] = useState(criterion.ordinal);
  const [comment, setComment] = useState(criterion.comment ?? "");
  const [isSensitive, setIsSensitive] = useState(criterion.isSensitive);
  /**
   * The rung the reviewer is LOOKING at, which is not always the one they have
   * chosen — WI-312.
   *
   * Kori Deitz, 2026-10-05: someone about to say "meets expectations" should be
   * able to read what "consistently exceeds" would have meant before settling.
   * So hover and keyboard focus drive the panel below, and fall back to the
   * selection when neither is active.
   */
  const [previewOrdinal, setPreviewOrdinal] = useState<number | null>(null);
  /** A comment typed but not yet sent — the 800ms pause hasn't elapsed. */
  const [typing, setTyping] = useState(false);

  /**
   * Typing saves on a pause rather than on every keystroke — and on blur, so a
   * comment finished with a click on "Hand in" is already stored by the time
   * the submit lands. Done with a timer rather than an effect on a debounced
   * value: the effect would have to depend on a mutation object that is new
   * every render.
   */
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Cancels a queued comment save.
   *
   * Every write below sends the WHOLE row, so a queued timer is not merely
   * redundant after another field changes — it is wrong. Its closure carries
   * the values from the render that scheduled it, so a rating chosen during
   * the 800ms window was silently reverted to `null` when the timer landed,
   * and nothing on screen showed it: the rating lives in local state and the
   * save only invalidates the task list, not this task. The reviewer found out
   * at "Hand in", told a criterion was unanswered that they could see was
   * answered. Any write must therefore cancel first.
   */
  const cancelPending = () => {
    if (pending.current) {
      clearTimeout(pending.current);
      pending.current = null;
    }
  };

  /**
   * The rung whose meaning the panel is showing: what they are looking at,
   * else what they have chosen, else nothing.
   */
  const sliderRungs = scale.filter((r) => r.ordinal !== SCALE_NOT_APPLICABLE_ORDINAL);
  const notApplicable = scale.find((r) => r.ordinal === SCALE_NOT_APPLICABLE_ORDINAL) ?? null;
  const shownRung = scale.find((r) => r.ordinal === (previewOrdinal ?? ordinal)) ?? null;

  /** The one write path. Takes the whole row so no caller can omit a field. */
  const persist = (next: { ordinal: number | null; comment: string; isSensitive: boolean }) => {
    if (locked) return;
    cancelPending();
    setTyping(false);
    save.mutate({
      questionId: criterion.questionId,
      input: {
        ordinal: next.ordinal,
        comment: next.comment || null,
        isSensitive: next.isSensitive,
      },
    });
  };

  /*
    What an unmount would otherwise lose. `onBlur` does not fire when a
    component goes away, so a comment typed and navigated away from inside the
    debounce window was dropped. Held in a ref because the unmount cleanup runs
    once and would otherwise close over the first render's values.
  */
  const latest = useRef({ ordinal, comment, isSensitive, saved: criterion.comment ?? "" });
  useEffect(() => {
    latest.current = { ordinal, comment, isSensitive, saved: criterion.comment ?? "" };
  }, [ordinal, comment, isSensitive, criterion.comment]);

  const persistRef = useRef(persist);
  useEffect(() => {
    persistRef.current = persist;
  });

  useEffect(() => {
    return () => {
      if (!pending.current) return;
      clearTimeout(pending.current);
      pending.current = null;
      const { ordinal: o, comment: c, isSensitive: s, saved } = latest.current;
      if (c !== saved) persistRef.current({ ordinal: o, comment: c, isSensitive: s });
    };
  }, []);

  const onCommentChange = (next: string) => {
    setComment(next);
    cancelPending();
    setTyping(true);
    pending.current = setTimeout(() => {
      // Read through the ref: by the time this lands, another field may have
      // moved on, and the row write must carry its current value, not this
      // render's.
      const { ordinal: o, isSensitive: s } = latest.current;
      persist({ ordinal: o, comment: next, isSensitive: s });
    }, 800);
  };

  const flushComment = () => {
    cancelPending();
    setTyping(false);
    if (comment !== (criterion.comment ?? "")) persist({ ordinal, comment, isSensitive });
  };

  const choose = (next: number | null) => {
    setOrdinal(next);
    onAnswer(next);
    persist({ ordinal: next, comment, isSensitive });
  };

  /*
    Says, per question, whether what is on screen is stored. Nothing at all
    on a question nobody has touched — "Saved" there would claim an answer.
  */
  const hasAnything = ordinal !== null || comment.trim() !== "" || isSensitive;
  const saveStatus = locked
    ? null
    : typing || save.isPending
      ? { tone: "neutral" as const, label: "Saving…" }
      : save.isError
        ? { tone: "danger" as const, label: "Not saved" }
        : hasAnything
          ? { tone: "success" as const, label: "Saved" }
          : null;

  return (
    <DetailSection
      title={criterion.name}
      headerActions={
        <div className="flex items-center gap-2">
          {saveStatus && (
            <span aria-live="polite">
              <StatusPill tone={saveStatus.tone} label={saveStatus.label} />
            </span>
          )}
          {criterion.categoryName && (
            <span className="flex items-center gap-1.5 text-muted-foreground text-sm">
              {categoryColorVar(criterion.categoryColor) && (
                <span
                  className="size-2.5 rounded-full"
                  style={{
                    backgroundColor: categoryColorVar(criterion.categoryColor) ?? undefined,
                  }}
                  aria-hidden
                />
              )}
              {criterion.categoryName}
            </span>
          )}
          {criterion.isRequired && <StatusPill tone="neutral" label="Required" />}
          {/*
            Said on the question itself, not in a footnote: a manager writing
            about promotion readiness needs to know, as they write it, that
            the person will never read this.
          */}
          {criterion.visibility === "people_ops" && (
            <StatusPill tone="info" label="People Ops only" />
          )}
        </div>
      }
    >
      {criterion.description && (
        <p className="mb-4 text-muted-foreground text-sm">{criterion.description}</p>
      )}

      <RatingSlider
        rungs={sliderRungs}
        notApplicable={notApplicable}
        value={ordinal}
        onChange={choose}
        onPreview={setPreviewOrdinal}
        disabled={locked}
        label={`Rating for ${criterion.name}`}
        describedBy={expectationPanelId}
      />

      {/*
        What the rating in front of them MEANS — WI-312.

        One panel rather than the wording repeated inside all five buttons.
        That was the shape until 2026-10-06 and it made a wall of text nobody
        read: five paragraphs side by side, under every question, under every
        criterion. Kori Deitz asked for it to be dynamic instead — the reviewer
        looks at one rung at a time and reads what it would mean for THIS
        person's role and level.

        `min-h` is deliberate: without it the card jumps as the reviewer moves
        across the rungs, which is worse than the empty space.
      */}
      <div
        id={expectationPanelId}
        aria-live="polite"
        // Full-strength text on a tinted card: the dashed, muted version was
        // hard to read, and this is the text the reviewer is meant to read.
        className={`mb-4 min-h-16 rounded-lg border p-3 ${
          shownRung
            ? "border-primary/40 border-l-4 border-l-primary bg-primary/5"
            : "border-border border-dashed bg-muted/40"
        }`}
      >
        {shownRung ? (
          <>
            <p className="font-semibold text-sm">{shownRung.name}</p>
            <p className="mt-1 text-foreground text-sm">
              {criterion.expectations[shownRung.ordinal] ??
                shownRung.description ??
                "No description recorded for this rating."}
            </p>
          </>
        ) : (
          <p className="text-foreground/80 text-sm">
            Choose a rating — or hover one — to see what it means for this role and level.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${sensitiveId}-comment`}>Comment (optional)</Label>
        <Textarea
          id={`${sensitiveId}-comment`}
          value={comment}
          disabled={locked}
          rows={3}
          placeholder="What led you to that rating?"
          onChange={(e) => onCommentChange(e.target.value)}
          onBlur={flushComment}
        />
        {!locked && (
          <div className="flex items-center gap-2">
            <Checkbox
              id={sensitiveId}
              checked={isSensitive}
              onCheckedChange={(checked) => {
                const next = checked === true;
                setIsSensitive(next);
                persist({ ordinal, comment, isSensitive: next });
              }}
            />
            <Label htmlFor={sensitiveId} className="cursor-pointer font-normal text-sm">
              Handle this comment sensitively
              {/*
                Said explicitly: people reach for this expecting anonymity, and
                it is not that.
              */}
              <span className="block text-muted-foreground text-xs">
                Restricts where it appears in reporting. Your name is still recorded against it.
              </span>
            </Label>
          </div>
        )}
      </div>
    </DetailSection>
  );
}
